/* Presentation only: existing WristMatch calculation and storage contracts are unchanged. */
(() => {
  const update = () => {
    const output = document.querySelector('[data-mr-home-profile]');
    if (!output) return;
    try {
      const mm = Number(localStorage.getItem('mr_wristmatch_wrist_mm'));
      const preference = localStorage.getItem('mr_wristmatch_preference');
      const unit = localStorage.getItem('mr_wristmatch_unit');
      const labels = { compact: 'Compact', balanced: 'Balanced', bold: 'Bold' };
      if (!Number.isFinite(mm) || mm < 100 || mm > 300 || !labels[preference]) { output.hidden = true; return; }
      const measurement = unit === 'in' ? (mm / 25.4).toFixed(1) + ' in' : (mm / 10).toFixed(1).replace(/\.0$/, '') + ' cm';
      output.textContent = 'Your saved profile: ' + measurement + ' · ' + labels[preference] + '. Ready to explore.';
      output.hidden = false;
    } catch { output.hidden = true; }
  };
  update();
  window.addEventListener('storage', update);
  document.addEventListener('shopify:section:load', update);
})();

/* Small, progressive carousel controls. Native horizontal scrolling remains available without JavaScript. */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const setupCarousel = (carousel) => {
    if (carousel.dataset.mrCarouselReady === 'true') return;
    const viewport = carousel.querySelector('[data-mr-carousel-viewport]');
    const previous = carousel.querySelector('[data-mr-carousel-previous]');
    const next = carousel.querySelector('[data-mr-carousel-next]');
    if (!viewport || !previous || !next) return;

    const updateControls = () => {
      const max = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      previous.disabled = viewport.scrollLeft <= 2;
      next.disabled = viewport.scrollLeft >= max - 2;
    };

    const move = (direction) => {
      const firstCard = viewport.firstElementChild;
      if (!firstCard) return;
      const gap = Number.parseFloat(getComputedStyle(viewport).columnGap) || 0;
      viewport.scrollBy({ left: direction * (firstCard.getBoundingClientRect().width + gap), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    };

    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    viewport.addEventListener('scroll', updateControls, { passive: true });
    window.addEventListener('resize', updateControls, { passive: true });
    carousel.dataset.mrCarouselReady = 'true';
    updateControls();
  };

  const setupAll = (root = document) => root.querySelectorAll('[data-mr-carousel]').forEach(setupCarousel);
  setupAll();
  document.addEventListener('shopify:section:load', (event) => setupAll(event.target));
})();


/* Lightweight, accessible hero carousel. */
(() => {
  const setupHero = (hero) => {
    if (hero.dataset.mrHeroReady === 'true') return;
    const slides = [...hero.querySelectorAll('[data-mr-hero-slide]')];
    const dots = [...hero.querySelectorAll('[data-mr-hero-dot]')];
    const previous = hero.querySelector('[data-mr-hero-previous]');
    const next = hero.querySelector('[data-mr-hero-next]');
    const status = hero.querySelector('[data-mr-hero-status]');
    if (slides.length < 2) {
      hero.dataset.mrHeroReady = 'true';
      return;
    }

    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let timer = null;
    let pointerStart = null;
    let hovering = false;
    let focusWithin = false;
    let destroyed = false;
    const controller = new AbortController();
    const { signal } = controller;
    const autoplay = hero.dataset.mrHeroAutoplay === 'true';
    const pauseOnHover = hero.dataset.mrHeroPauseOnHover === 'true';
    const delay = Math.max(3000, Number.parseInt(hero.dataset.mrHeroDelay, 10) || 5000);

    const stopTimer = () => {
      if (timer) window.clearTimeout(timer);
      timer = null;
    };

    const canAutoplay = () => autoplay && !destroyed && !hovering && !focusWithin && !document.hidden;

    const schedule = () => {
      stopTimer();
      if (!canAutoplay()) return;
      timer = window.setTimeout(() => showSlide(activeIndex + 1, false), delay);
    };

    const showSlide = (requestedIndex, announce = true) => {
      activeIndex = (requestedIndex + slides.length) % slides.length;
      slides.forEach((slide, index) => {
        const active = index === activeIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.inert = !active;
      });
      dots.forEach((dot, index) => {
        const active = index === activeIndex;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (status && announce) status.textContent = 'Image ' + (activeIndex + 1) + ' of ' + slides.length;
      schedule();
    };

    const showManualSlide = (requestedIndex) => {
      hovering = false;
      focusWithin = Boolean(hero.querySelector(':focus-visible'));
      showSlide(requestedIndex);
    };

    previous?.addEventListener('click', () => showManualSlide(activeIndex - 1), { signal });
    next?.addEventListener('click', () => showManualSlide(activeIndex + 1), { signal });
    dots.forEach((dot, index) => dot.addEventListener('click', () => showManualSlide(index), { signal }));

    if (pauseOnHover) {
      hero.addEventListener('mouseenter', () => {
        hovering = true;
        stopTimer();
      }, { signal });
      hero.addEventListener('mouseleave', () => {
        hovering = false;
        schedule();
      }, { signal });
    }
    const syncFocusPause = () => {
      focusWithin = Boolean(hero.querySelector(':focus-visible'));
      if (focusWithin) stopTimer();
      else schedule();
    };
    hero.addEventListener('focusin', () => window.setTimeout(syncFocusPause, 0), { signal });
    hero.addEventListener('focusout', () => window.setTimeout(syncFocusPause, 0), { signal });

    hero.addEventListener('touchstart', (event) => {
      pointerStart = event.changedTouches[0]?.clientX ?? null;
      stopTimer();
    }, { passive: true, signal });
    hero.addEventListener('touchend', (event) => {
      if (pointerStart === null) return;
      const distance = (event.changedTouches[0]?.clientX ?? pointerStart) - pointerStart;
      pointerStart = null;
      if (Math.abs(distance) > 45) showSlide(activeIndex + (distance < 0 ? 1 : -1));
      else schedule();
    }, { passive: true, signal });
    hero.addEventListener('touchcancel', () => {
      pointerStart = null;
      schedule();
    }, { passive: true, signal });

    hero.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        showSlide(activeIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        showSlide(activeIndex + 1);
      }
    }, { signal });

    document.addEventListener('visibilitychange', schedule, { signal });

    hero.mrHeroShowSlide = showSlide;
    hero.mrHeroCleanup = () => {
      destroyed = true;
      stopTimer();
      controller.abort();
      delete hero.mrHeroShowSlide;
      delete hero.mrHeroCleanup;
      delete hero.dataset.mrHeroReady;
    };
    hero.dataset.mrHeroReady = 'true';
    showSlide(activeIndex, false);
  };

  const findHeroes = (root = document) => [
    ...(root.matches?.('[data-mr-hero]') ? [root] : []),
    ...root.querySelectorAll('[data-mr-hero]')
  ];
  const setupAll = (root = document) => findHeroes(root).forEach(setupHero);
  setupAll();
  document.addEventListener('shopify:section:load', (event) => setupAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    findHeroes(event.target).forEach((hero) => hero.mrHeroCleanup?.());
  });
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest?.('[data-mr-hero-slide]');
    const hero = slide?.closest('[data-mr-hero]');
    if (!slide || !hero) return;
    const slides = [...hero.querySelectorAll('[data-mr-hero-slide]')];
    hero.mrHeroShowSlide?.(slides.indexOf(slide));
  });
})();
